import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Calculator,
  Check,
  ImagePlus,
  Link2,
  RefreshCw,
  Save,
} from "lucide-react";
import Swal from "sweetalert2";
import { Apis } from "../../../lib/Apis";
import { useBlog } from "../useHook";
import { BLOG_STATUS_OPTIONS } from "../../../Utils/staticDropdownOptions";
import {
  ImageSeoFields,
  appendPrefixedImageSeoToFormData,
} from "../../../Components/Shared/ImageSeoFields";
import { UrduInput } from "../../../Components/Shared/UrduInput";
import {
  HOUSE_COMPARISON_CATEGORY,
  HOUSE_TYPES,
  MARLA_OPTIONS,
  QUALITIES,
  QUALITY_STYLE,
  buildCustomUrl,
  buildShortDescription,
  buildShortDescriptionUr,
  buildTitle,
  buildTitleUr,
  formatPct,
  formatPkr,
  isSideComplete,
  pickTable,
  sizeDisplayFromMarla,
  sizeOptionLabel,
  slugify,
} from "./comparisonBlogConfig";
import {
  COMPARISON_COVER_IMAGES,
  fileFromComparisonCover,
} from "./comparisonCoverImages";

const emptySide = { marla: "", htId: "", quality: "" };

const SideCard = ({
  title,
  side,
  onChange,
  accent,
  mode = "marla",
  disabled = false,
}) => {
  const qStyle = side.quality
    ? QUALITY_STYLE[side.quality] || QUALITY_STYLE.budget
    : null;
  const sizeFieldLabel = mode === "sqYards" ? "Sq Yards" : "Marla";
  const lockedClass = disabled
    ? "ui-input-field disabled:bg-gray-100 disabled:cursor-not-allowed"
    : "ui-input-field";
  return (
    <div
      className={`rounded-2xl border bg-white p-4 shadow-sm ${disabled ? "opacity-90" : ""}`}
      style={{ borderColor: accent }}
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-sm font-extrabold text-[#15173d]">{title}</h3>
        {qStyle && (
          <span
            className="rounded-full px-2.5 py-0.5 text-[10px] font-bold"
            style={{ background: qStyle.bg, color: qStyle.color }}
          >
            {QUALITIES.find((q) => q.key === side.quality)?.label}
          </span>
        )}
      </div>
      <div className="space-y-3">
        <label className="block">
          <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-gray-400">
            {sizeFieldLabel}
          </span>
          <select
            value={side.marla}
            onChange={(e) => onChange({ ...side, marla: e.target.value })}
            className={lockedClass}
            disabled={disabled}
          >
            <option value="">
              {mode === "sqYards" ? "Select sq yards" : "Select marla"}
            </option>
            {MARLA_OPTIONS.map((m) => (
              <option key={m.key} value={m.key}>
                {sizeOptionLabel(m, mode)}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-gray-400">
            House type
          </span>
          <select
            value={side.htId}
            onChange={(e) =>
              onChange({
                ...side,
                htId: e.target.value ? Number(e.target.value) : "",
              })
            }
            className={lockedClass}
            disabled={disabled}
          >
            <option value="">Select house type</option>
            {HOUSE_TYPES.map((ht) => (
              <option key={ht.id} value={ht.id}>
                {ht.id}. {ht.label}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-gray-400">
            Quality
          </span>
          <select
            value={side.quality}
            onChange={(e) => onChange({ ...side, quality: e.target.value })}
            className={lockedClass}
            disabled={disabled}
          >
            <option value="">Select quality</option>
            {QUALITIES.map((q) => (
              <option key={q.key} value={q.key}>
                {q.label}
              </option>
            ))}
          </select>
        </label>
      </div>
    </div>
  );
};

const ComparisonBlogForm = ({ isEditMode = false }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { getBlogCategories, postBlog, updateBlog, getBlogById } = useBlog();

  const [categoryId, setCategoryId] = useState("");
  const [name, setName] = useState("");
  const [nameUr, setNameUr] = useState("");
  const [status, setStatus] = useState("published");
  const [customUrl, setCustomUrl] = useState("");
  const [description, setDescription] = useState("");
  const [descriptionUr, setDescriptionUr] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [nameUrTouched, setNameUrTouched] = useState(false);
  const [descTouched, setDescTouched] = useState(false);
  const [descUrTouched, setDescUrTouched] = useState(false);
  const [urlTouched, setUrlTouched] = useState(false);
  const [seoTouched, setSeoTouched] = useState(false);
  const [headerImage, setHeaderImage] = useState(null);
  const [selectedCoverId, setSelectedCoverId] = useState("");
  const [headerImageAlt, setHeaderImageAlt] = useState("");
  const [headerImageTitle, setHeaderImageTitle] = useState("");
  const [sideA, setSideA] = useState({ ...emptySide });
  const [sideB, setSideB] = useState({ ...emptySide });
  const [mode, setMode] = useState("marla");
  const [loading, setLoading] = useState(false);
  const [loadingBlog, setLoadingBlog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tableA, setTableA] = useState(null);
  const [tableB, setTableB] = useState(null);
  const [displayA, setDisplayA] = useState("");
  const [displayB, setDisplayB] = useState("");
  const [linkedOk, setLinkedOk] = useState(false);

  const bothSelected = isSideComplete(sideA) && isSideComplete(sideB);

  const htA = useMemo(
    () => HOUSE_TYPES.find((h) => h.id === Number(sideA.htId)) || null,
    [sideA.htId],
  );
  const htB = useMemo(
    () => HOUSE_TYPES.find((h) => h.id === Number(sideB.htId)) || null,
    [sideB.htId],
  );
  const qA = QUALITIES.find((q) => q.key === sideA.quality)?.label || "";
  const qB = QUALITIES.find((q) => q.key === sideB.quality)?.label || "";

  const autoTitle = useMemo(() => {
    if (!bothSelected) return "";
    return buildTitle(sideA, sideB, mode);
  }, [sideA, sideB, mode, bothSelected]);

  const autoTitleUr = useMemo(() => {
    if (!bothSelected) return "";
    return buildTitleUr(sideA, sideB, mode);
  }, [sideA, sideB, mode, bothSelected]);

  const autoDescription = useMemo(() => {
    if (!bothSelected) return "";
    return buildShortDescription(sideA, sideB, mode);
  }, [sideA, sideB, mode, bothSelected]);

  const autoDescriptionUr = useMemo(() => {
    if (!bothSelected) return "";
    return buildShortDescriptionUr(sideA, sideB, mode);
  }, [sideA, sideB, mode, bothSelected]);

  const autoUrl = useMemo(() => {
    if (!bothSelected) return "";
    return buildCustomUrl(sideA, sideB, mode);
  }, [sideA, sideB, mode, bothSelected]);

  // Auto-fill EN + UR title/description + URL when all 6 fields are selected.
  useEffect(() => {
    if (!bothSelected || isEditMode) return;
    if (!nameTouched && autoTitle) setName(autoTitle);
    if (!nameUrTouched && autoTitleUr) setNameUr(autoTitleUr);
    if (!descTouched && autoDescription) setDescription(autoDescription);
    if (!descUrTouched && autoDescriptionUr) setDescriptionUr(autoDescriptionUr);
    if (!urlTouched && autoUrl) setCustomUrl(autoUrl);
  }, [
    bothSelected,
    isEditMode,
    autoTitle,
    autoTitleUr,
    autoDescription,
    autoDescriptionUr,
    autoUrl,
    nameTouched,
    nameUrTouched,
    descTouched,
    descUrTouched,
    urlTouched,
  ]);

  // Keep image alt/title in sync with title (same as other blogs) until user edits SEO.
  useEffect(() => {
    if (seoTouched || !name.trim()) return;
    setHeaderImageAlt(name.trim());
    setHeaderImageTitle(name.trim());
  }, [name, seoTouched]);

  // If user edited title but not URL, keep URL in sync with title slug.
  useEffect(() => {
    if (isEditMode || urlTouched || !name.trim()) return;
    if (nameTouched) {
      setCustomUrl(`${slugify(name)}/`);
    }
  }, [name, nameTouched, urlTouched, isEditMode]);

  const selectCoverImage = (cover) => {
    setSelectedCoverId(cover.id);
    setHeaderImage({
      preview: cover.src,
      file: null,
      assetId: cover.id,
    });
  };

  const comparison = useMemo(() => {
    const greyA = Number(tableA?.totGrey) || 0;
    const finishA = Number(tableA?.totFinish) || 0;
    const totalA = Number(tableA?.grandTotal) || greyA + finishA;
    const greyB = Number(tableB?.totGrey) || 0;
    const finishB = Number(tableB?.totFinish) || 0;
    const totalB = Number(tableB?.grandTotal) || greyB + finishB;
    const dGrey = greyB - greyA;
    const dFinish = finishB - finishA;
    const dTotal = totalB - totalA;
    const pct = totalA > 0 ? (dTotal / totalA) * 100 : null;
    return {
      greyA,
      finishA,
      totalA,
      greyB,
      finishB,
      totalB,
      dGrey,
      dFinish,
      dTotal,
      pct,
    };
  }, [tableA, tableB]);

  const loadComparison = useCallback(async () => {
    if (!isSideComplete(sideA) || !isSideComplete(sideB)) {
      setTableA(null);
      setTableB(null);
      setDisplayA("");
      setDisplayB("");
      setLinkedOk(false);
      return;
    }

    setLoading(true);
    setLinkedOk(false);
    try {
      const marlaA = Number(sideA.marla);
      const marlaB = Number(sideB.marla);
      const sameMarla = marlaA === marlaB;

      const resA = await Apis.getCostConstructionTables(marlaA, mode);
      const dataA = resA?.data?.data || resA?.data || {};
      const tablesA = dataA.tables || [];
      const pickedA = pickTable(tablesA, sideA.htId, sideA.quality);

      let tablesB = tablesA;
      let dataB = dataA;
      if (!sameMarla) {
        const resB = await Apis.getCostConstructionTables(marlaB, mode);
        dataB = resB?.data?.data || resB?.data || {};
        tablesB = dataB.tables || [];
      }
      const pickedB = pickTable(tablesB, sideB.htId, sideB.quality);

      setTableA(pickedA);
      setTableB(pickedB);
      setDisplayA(
        dataA.displayStr || sizeDisplayFromMarla(marlaA, mode),
      );
      setDisplayB(
        dataB.displayStr || sizeDisplayFromMarla(marlaB, mode),
      );

      if (!pickedA || !pickedB) {
        setLinkedOk(false);
        await Swal.fire({
          icon: "warning",
          title: "Table not found",
          text: "One or both sides have no matching cost table for this marla / house type / quality.",
        });
        return;
      }
      setLinkedOk(true);
    } catch (err) {
      console.error(err);
      setLinkedOk(false);
      Swal.fire({
        icon: "error",
        title: "Failed to load tables",
        text: err?.message || "Could not fetch cost tables.",
      });
    } finally {
      setLoading(false);
    }
  }, [sideA, sideB, mode]);

  const switchMode = (next) => {
    if (next === mode) return;
    setMode(next);
    setLinkedOk(false);
    setTableA(null);
    setTableB(null);
    setDisplayA("");
    setDisplayB("");
  };

  useEffect(() => {
    (async () => {
      try {
        const categories = await getBlogCategories();
        const list = Array.isArray(categories) ? categories : [];
        const found = list.find(
          (c) =>
            String(c.name || "")
              .toLowerCase()
              .trim() === HOUSE_COMPARISON_CATEGORY.toLowerCase(),
        );
        if (found) {
          setCategoryId(String(found.id));
        } else {
          Swal.fire({
            icon: "warning",
            title: "Category missing",
            text: `Please create a blog category named "${HOUSE_COMPARISON_CATEGORY}" first.`,
          });
        }
      } catch (e) {
        console.error(e);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isEditMode || !id) return;
    (async () => {
      setLoadingBlog(true);
      try {
        const data = await getBlogById(id);
        const blog = data?.blog || data?.blogData || data;
        if (!blog?.id && !blog?.name) throw new Error("Blog not found");
        setName(blog.name || "");
        setNameUr(blog.name_ur || "");
        setStatus(blog.status || "draft");
        setCustomUrl(blog.customUrl || "");
        setDescription(blog.description || "");
        setDescriptionUr(blog.description_ur || "");
        // Preserve saved values — don't overwrite with auto-generated text on edit.
        setNameTouched(true);
        setNameUrTouched(true);
        setDescTouched(true);
        setDescUrTouched(true);
        setUrlTouched(true);
        setSeoTouched(true);
        setHeaderImage(
          blog.headerImage
            ? { preview: blog.headerImage, file: null, assetId: null }
            : null,
        );
        setSelectedCoverId("");
        setHeaderImageAlt(blog.header_image_alt || "");
        setHeaderImageTitle(blog.header_image_title || "");
        setSideA({
          marla: blog.cmp_marla_a != null ? String(Number(blog.cmp_marla_a)) : "",
          htId: blog.cmp_ht_a != null ? Number(blog.cmp_ht_a) : "",
          quality: blog.cmp_quality_a || "",
        });
        setSideB({
          marla: blog.cmp_marla_b != null ? String(Number(blog.cmp_marla_b)) : "",
          htId: blog.cmp_ht_b != null ? Number(blog.cmp_ht_b) : "",
          quality: blog.cmp_quality_b || "",
        });
        setMode(blog.cmp_mode || "marla");
        if (blog.categoryId) setCategoryId(String(blog.categoryId));
      } catch (e) {
        console.error(e);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load comparison blog.",
        });
        navigate("/comparison-blogs/list");
      } finally {
        setLoadingBlog(false);
      }
    })();
  }, [isEditMode, id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-preview when both sides + mode ready (edit hydrate or mode switch)
  useEffect(() => {
    if (!bothSelected) return;
    loadComparison();
  }, [bothSelected, mode, loadComparison]);

  const hasHeaderImage = Boolean(
    headerImage?.file ||
      headerImage?.preview ||
      (typeof headerImage === "string" && headerImage),
  );

  const canSave = isEditMode
    ? Boolean(hasHeaderImage && description.trim() && !saving)
    : Boolean(
        categoryId &&
          name.trim() &&
          customUrl.trim() &&
          description.trim() &&
          hasHeaderImage &&
          bothSelected &&
          linkedOk &&
          !saving,
      );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSave) {
      Swal.fire({
        icon: "info",
        title: "Complete the form",
        text: isEditMode
          ? "Cover image and short description (EN) are required."
          : "Select a cover image, fill title/URL/description, select both sides, and link the comparison table.",
      });
      return;
    }

    setSaving(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append("name", name.trim());
      formDataObj.append("name_ur", nameUr.trim());
      formDataObj.append("description", description.trim());
      formDataObj.append("description_ur", descriptionUr.trim());
      formDataObj.append("status", status);
      formDataObj.append(
        "status_ur",
        status === "published" ? "شائع شدہ" : "مسودہ",
      );
      formDataObj.append("categoryId", categoryId);
      formDataObj.append(
        "customUrl",
        customUrl.endsWith("/") ? customUrl : `${customUrl}/`,
      );
      formDataObj.append("content", JSON.stringify([]));
      formDataObj.append("content_ur", JSON.stringify([]));
      // Clear cost linkage — this is a comparison blog
      formDataObj.append("cost_marla", "");
      formDataObj.append("cost_mode", "");
      // Link comparison table (same pattern as cost_marla / cost_mode)
      formDataObj.append("cmp_marla_a", String(sideA.marla));
      formDataObj.append("cmp_ht_a", String(sideA.htId));
      formDataObj.append("cmp_quality_a", sideA.quality);
      formDataObj.append("cmp_marla_b", String(sideB.marla));
      formDataObj.append("cmp_ht_b", String(sideB.htId));
      formDataObj.append("cmp_quality_b", sideB.quality);
      formDataObj.append("cmp_mode", mode || "marla");

      appendPrefixedImageSeoToFormData(
        formDataObj,
        "header_image",
        headerImageAlt,
        headerImageTitle,
      );

      if (headerImage?.assetId) {
        const cover = COMPARISON_COVER_IMAGES.find(
          (c) => c.id === headerImage.assetId,
        );
        if (!cover) throw new Error("Selected cover image not found.");
        const file = await fileFromComparisonCover(cover);
        formDataObj.append("headerImage", file);
      } else if (headerImage?.file) {
        formDataObj.append("headerImage", headerImage.file);
      } else if (!isEditMode) {
        throw new Error("Please select a cover image.");
      }

      if (isEditMode && id) {
        await updateBlog(id, formDataObj);
      } else {
        const created = await postBlog(formDataObj);
        const blog = created?.blogData || created;
        if (!blog?.id) {
          throw new Error("Blog created but id was not returned.");
        }
      }

      await Swal.fire({
        title: "Success!",
        text: isEditMode
          ? "Comparison blog updated — table linked."
          : "Comparison blog created — table linked.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
      navigate("/comparison-blogs/list");
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          err?.response?.data?.message ||
          err?.message ||
          (isEditMode ? "Failed to update." : "Failed to create."),
      });
    } finally {
      setSaving(false);
    }
  };

  const labelA = bothSelected
    ? `${displayA || sizeDisplayFromMarla(sideA.marla, mode)} · ${htA?.label || "—"} · ${qA}`
    : "Select Side A";
  const labelB = bothSelected
    ? `${displayB || sizeDisplayFromMarla(sideB.marla, mode)} · ${htB?.label || "—"} · ${qB}`
    : "Select Side B";

  if (loadingBlog) {
    return (
      <div className="py-16 text-center text-sm text-gray-400">
        Loading comparison blog…
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5 pb-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={() => navigate("/comparison-blogs/list")}
            className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-xl border border-[#15173d]/10 bg-white text-[#15173d] hover:bg-[#faf8fa]"
          >
            <ArrowLeft size={16} />
          </button>
          <div>
            <h2 className="text-xl font-extrabold text-[#15173d]">
              {isEditMode ? "Edit Comparison Blog" : "Create Comparison Blog"}
            </h2>
            <p className="text-xs text-gray-500">
              {isEditMode
                ? "Only cover image, image alt/title, and short descriptions (EN/UR) can be changed."
                : "Select A vs B fields, link the live cost table (same as Cost of Construction blogs), then save."}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {!isEditMode && (
            <button
              type="button"
              className="ui-btn-save"
              onClick={loadComparison}
              disabled={loading || !bothSelected}
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              {loading ? "Linking…" : "Link / Refresh Table"}
            </button>
          )}
          <button type="submit" className="ui-btn-save" disabled={!canSave}>
            <Save size={14} />
            {saving ? "Saving…" : isEditMode ? "Update" : "Create & Link"}
          </button>
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-xl border border-[#982598]/15 bg-[#f5eeff] px-4 py-3">
        <Calculator size={14} className="mt-0.5 shrink-0 text-[#982598]" />
        <p className="text-[11px] font-medium text-[#982598]">
          {isEditMode ? (
            <>
              Comparison fields, title, URL and status are <strong>locked</strong>.
              You can only edit <strong>cover image</strong>,{" "}
              <strong>image alt/title</strong>, and{" "}
              <strong>short description (EN + UR)</strong>.
            </>
          ) : (
            <>
              Category is auto-set to <strong>{HOUSE_COMPARISON_CATEGORY}</strong>.
              Select all 6 comparison fields — <strong>title</strong>,{" "}
              <strong>description</strong> and <strong>custom URL</strong> fill
              automatically. Then click <strong>Link / Refresh Table</strong> to
              attach live rates.
            </>
          )}
        </p>
      </div>

      {/* Comparison fields first — title/desc/URL auto-fill from these */}
      <section className="ui-card space-y-4">
        <div className="flex items-center gap-2.5">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-[#982598]/10 text-[#982598]">
            <Calculator size={15} />
          </span>
          <div>
            <h3 className="text-[14px] font-extrabold text-[#15173d]">
              Comparison Table Linkage
            </h3>
            <p className="text-[11px] text-gray-500">
              {isEditMode
                ? "Locked on edit — comparison linkage cannot be changed"
                : "Select all 6 fields — title, description & URL auto-generate below"}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
            Display mode
          </p>
          <button
            type="button"
            onClick={() => !isEditMode && switchMode("marla")}
            disabled={isEditMode}
            className={`rounded-full border px-3.5 py-1.5 text-xs font-bold transition-colors ${
              mode === "marla"
                ? "border-[#982598] bg-[#f5eeff] text-[#982598]"
                : "border-gray-200 bg-white text-gray-500"
            } ${isEditMode ? "cursor-not-allowed opacity-70" : ""}`}
          >
            Marla
          </button>
          <button
            type="button"
            onClick={() => !isEditMode && switchMode("sqYards")}
            disabled={isEditMode}
            className={`rounded-full border px-3.5 py-1.5 text-xs font-bold transition-colors ${
              mode === "sqYards"
                ? "border-[#982598] bg-[#f5eeff] text-[#982598]"
                : "border-gray-200 bg-white text-gray-500"
            } ${isEditMode ? "cursor-not-allowed opacity-70" : ""}`}
          >
            Sq Yards
          </button>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <SideCard
            title="House type 1 (Side A)"
            side={sideA}
            onChange={setSideA}
            accent="rgba(152,37,152,0.25)"
            mode={mode}
            disabled={isEditMode}
          />
          <SideCard
            title="House type 2 (Side B)"
            side={sideB}
            onChange={setSideB}
            accent="rgba(21,23,61,0.2)"
            mode={mode}
            disabled={isEditMode}
          />
        </div>
      </section>

      {/* Cover image — pick from preset gallery */}
      <section className="ui-card space-y-4">
        <div className="flex items-center gap-2.5">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-[#982598]/10 text-[#982598]">
            <ImagePlus size={15} />
          </span>
          <div>
            <h3 className="text-[14px] font-extrabold text-[#15173d]">
              Cover image
            </h3>
            <p className="text-[11px] text-gray-500">
              Select one of the 5 preset comparison covers
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {COMPARISON_COVER_IMAGES.map((cover) => {
            const isSelected = selectedCoverId === cover.id;
            return (
              <button
                key={cover.id}
                type="button"
                onClick={() => selectCoverImage(cover)}
                className={`group relative overflow-hidden rounded-xl border-2 text-left transition ${
                  isSelected
                    ? "border-[#982598] shadow-[0_0_0_3px_rgba(152,37,152,0.18)]"
                    : "border-[#15173d]/10 hover:border-[#982598]/40"
                }`}
              >
                <img
                  src={cover.src}
                  alt={cover.label}
                  className="aspect-[1200/630] h-auto w-full object-cover"
                />
                <span
                  className={`absolute inset-x-0 bottom-0 px-2 py-1.5 text-[10px] font-bold ${
                    isSelected
                      ? "bg-[#982598] text-white"
                      : "bg-black/55 text-white group-hover:bg-black/70"
                  }`}
                >
                  {cover.label}
                </span>
                {isSelected && (
                  <span className="absolute right-2 top-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#982598] text-white shadow">
                    <Check size={12} strokeWidth={3} />
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {hasHeaderImage && (
          <div className="overflow-hidden rounded-xl border border-[#15173d]/10 bg-[#faf8fa]">
            <p className="border-b border-[#15173d]/5 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-gray-400">
              Selected preview
            </p>
            <img
              src={headerImage?.preview || headerImage}
              alt={headerImageAlt || "Cover preview"}
              title={headerImageTitle || undefined}
              className="max-h-[280px] w-full object-cover"
            />
          </div>
        )}

        <ImageSeoFields
          imageAlt={headerImageAlt}
          imageTitle={headerImageTitle}
          onAltChange={(val) => {
            setSeoTouched(true);
            setHeaderImageAlt(val);
          }}
          onTitleChange={(val) => {
            setSeoTouched(true);
            setHeaderImageTitle(val);
          }}
        />
      </section>

      {/* Auto-filled blog details */}
      <section className="ui-card space-y-4">
        <div className="flex items-center gap-2.5">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-[#982598]/10 text-[#982598]">
            <Link2 size={15} />
          </span>
          <div>
            <h3 className="text-[14px] font-extrabold text-[#15173d]">
              Blog details
            </h3>
            <p className="text-[11px] text-gray-500">
              {isEditMode
                ? "Only short descriptions (EN/UR) are editable — title, URL & status stay locked"
                : "Auto-generated from comparison fields — you can still edit"}
            </p>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="ui-form-label">Title (EN)</label>
            <input
              className="ui-input-field disabled:bg-gray-100 disabled:cursor-not-allowed"
              value={name}
              disabled={isEditMode}
              onChange={(e) => {
                setNameTouched(true);
                setName(e.target.value);
              }}
              placeholder="Auto-fills when both sides are selected"
            />
            <UrduInput
              label="عنوان (اردو)"
              name="name_ur"
              value={nameUr}
              disabled={isEditMode}
              onChange={(e) => {
                setNameUrTouched(true);
                setNameUr(e.target.value);
              }}
              placeholder="خودکار اردو عنوان"
            />
          </div>
          <div>
            <label className="ui-form-label">Status</label>
            <select
              className="ui-input-field disabled:bg-gray-100 disabled:cursor-not-allowed"
              value={status}
              disabled={isEditMode}
              onChange={(e) => setStatus(e.target.value)}
            >
              {BLOG_STATUS_OPTIONS.map((opt) => (
                <option key={opt.en} value={opt.en}>
                  {opt.enLabel}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="ui-form-label">Custom URL</label>
            <div className="flex overflow-hidden rounded-xl border border-[#15173d]/10">
              <span className="flex items-center bg-[#f3eef3] px-3 text-xs text-gray-500">
                /blog/
              </span>
              <input
                className="ui-input-field rounded-none border-0 disabled:bg-gray-100 disabled:cursor-not-allowed"
                value={customUrl}
                disabled={isEditMode}
                onChange={(e) => {
                  setUrlTouched(true);
                  setCustomUrl(e.target.value);
                }}
                placeholder="Auto-fills from title"
              />
            </div>
          </div>
          <div className="sm:col-span-2">
            <label className="ui-form-label">Short description (EN)</label>
            <textarea
              className="ui-input-field min-h-[88px]"
              value={description}
              onChange={(e) => {
                setDescTouched(true);
                setDescription(e.target.value);
              }}
              placeholder="Auto-fills when both sides are selected"
            />
            <UrduInput
              label="مختصر تفصیل (اردو)"
              name="description_ur"
              type="textarea"
              rows={4}
              value={descriptionUr}
              onChange={(e) => {
                setDescUrTouched(true);
                setDescriptionUr(e.target.value);
              }}
              placeholder="خودکار اردو تفصیل"
            />
          </div>
          <div>
            <label className="ui-form-label">Category</label>
            <input
              className="ui-input-field disabled:bg-gray-100"
              value={HOUSE_COMPARISON_CATEGORY}
              disabled
            />
          </div>
          <div>
            <label className="ui-form-label">Table link status</label>
            <div
              className={`rounded-xl border px-3 py-2 text-sm font-semibold ${
                linkedOk
                  ? "border-green-200 bg-green-50 text-green-800"
                  : "border-amber-200 bg-amber-50 text-amber-800"
              }`}
            >
              {linkedOk
                ? "Linked — live comparison table ready"
                : bothSelected
                  ? "Select fields, then Link / Refresh Table"
                  : "Select Side A and Side B first"}
            </div>
          </div>
        </div>
      </section>

      <div
        style={{
          borderRadius: 18,
          background: "linear-gradient(135deg,#15173d 0%,#2d1050 100%)",
          padding: "18px 24px",
          boxShadow: "0 8px 28px rgba(21,23,61,0.18)",
        }}
      >
        <p
          style={{
            fontSize: 10,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.15em",
            color: "rgba(255,255,255,0.45)",
            marginBottom: 6,
          }}
        >
          Linked comparison
        </p>
        <p style={{ fontSize: 15, fontWeight: 800, color: "#fff", lineHeight: 1.45 }}>
          {labelA}
        </p>
        <p
          style={{
            fontSize: 11,
            color: "rgba(255,255,255,0.45)",
            margin: "6px 0",
            fontWeight: 700,
          }}
        >
          vs
        </p>
        <p
          style={{
            fontSize: 15,
            fontWeight: 800,
            color: "#f0c6ff",
            lineHeight: 1.45,
          }}
        >
          {labelB}
        </p>
      </div>

      <div className="overflow-hidden rounded-2xl border border-[#15173d]/10 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse text-left text-sm">
            <thead>
              <tr className="bg-[#15173d] text-[11px] font-bold uppercase tracking-wider text-white/80">
                <th className="px-4 py-3">House type</th>
                <th className="px-4 py-3 text-right">Grey structure Cost</th>
                <th className="px-4 py-3 text-right">Finishing Costs</th>
                <th className="px-4 py-3 text-right">Total Costs</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="px-4 py-10 text-center text-gray-400">
                    Loading comparison…
                  </td>
                </tr>
              ) : !bothSelected || !linkedOk ? (
                <tr>
                  <td colSpan={4} className="px-4 py-10 text-center text-gray-400">
                    Select both sides and click Link / Refresh Table to attach
                    the comparison.
                  </td>
                </tr>
              ) : (
                <>
                  <tr className="border-b border-[#f0e4f0] bg-[#faf8fa]">
                    <td className="px-4 py-3.5">
                      <p className="font-extrabold text-[#15173d]">House type 1</p>
                      <p className="mt-0.5 text-[11px] text-gray-500">{labelA}</p>
                    </td>
                    <td className="px-4 py-3.5 text-right font-semibold tabular-nums">
                      {formatPkr(comparison.greyA)}
                    </td>
                    <td className="px-4 py-3.5 text-right font-semibold tabular-nums">
                      {formatPkr(comparison.finishA)}
                    </td>
                    <td className="px-4 py-3.5 text-right font-extrabold tabular-nums text-[#15173d]">
                      {formatPkr(comparison.totalA)}
                    </td>
                  </tr>
                  <tr className="border-b border-[#f0e4f0]">
                    <td className="px-4 py-3.5">
                      <p className="font-extrabold text-[#15173d]">House type 2</p>
                      <p className="mt-0.5 text-[11px] text-gray-500">{labelB}</p>
                    </td>
                    <td className="px-4 py-3.5 text-right font-semibold tabular-nums">
                      {formatPkr(comparison.greyB)}
                    </td>
                    <td className="px-4 py-3.5 text-right font-semibold tabular-nums">
                      {formatPkr(comparison.finishB)}
                    </td>
                    <td className="px-4 py-3.5 text-right font-extrabold tabular-nums text-[#15173d]">
                      {formatPkr(comparison.totalB)}
                    </td>
                  </tr>
                  <tr className="border-b border-[#f0e4f0] bg-[#f5eeff]/60">
                    <td className="px-4 py-3.5 font-extrabold text-[#982598]">
                      Difference
                    </td>
                    <td className="px-4 py-3.5 text-right font-semibold tabular-nums text-[#982598]">
                      {formatPkr(comparison.dGrey)}
                    </td>
                    <td className="px-4 py-3.5 text-right font-semibold tabular-nums text-[#982598]">
                      {formatPkr(comparison.dFinish)}
                    </td>
                    <td className="px-4 py-3.5 text-right font-extrabold tabular-nums text-[#982598]">
                      {formatPkr(comparison.dTotal)}
                    </td>
                  </tr>
                  <tr className="bg-[#15173d]/[0.03]">
                    <td className="px-4 py-3.5 font-extrabold text-[#15173d]">
                      Incremental Percentage cost
                    </td>
                    <td
                      colSpan={2}
                      className="px-4 py-3.5 text-right text-xs text-gray-400"
                    >
                      (Total B − Total A) / Total A
                    </td>
                    <td className="px-4 py-3.5 text-right text-lg font-black tabular-nums text-[#15173d]">
                      {formatPct(comparison.pct)}
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </form>
  );
};

export default ComparisonBlogForm;
